import { useState, useEffect, useMemo } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, labelStyle, outlineButton } from './adminTheme';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const T = {
    fr: {
        heading: 'Gestion des rôles — Creator ↔ Member',
        note: 'Les comptes ADMIN et ORGANISATION ne sont pas listés ici.',
        search: 'Rechercher un membre...',
        loading: 'Chargement...',
        empty: 'Aucun résultat.',
        toMember: 'Passer Member',
        toCreator: 'Passer Creator',
        error: (msg) => `Erreur : ${msg}`,
        updated: (username, role) => `${username} est maintenant ${role}.`,
    },
    en: {
        heading: 'Role management — Creator ↔ Member',
        note: 'ADMIN and ORGANISATION accounts are not listed here.',
        search: 'Search a member...',
        loading: 'Loading...',
        empty: 'No results.',
        toMember: 'Switch to Member',
        toCreator: 'Switch to Creator',
        error: (msg) => `Error: ${msg}`,
        updated: (username, role) => `${username} is now ${role}.`,
    },
};

function AdminRoles() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [updatingId, setUpdatingId] = useState(null);
    const [statusMsg, setStatusMsg] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        const checkRole = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) return;
            const { data } = await supabase.from('users').select('role').eq('user_id', session.user.id).single();
            if (data?.role?.toLowerCase() === 'admin') setIsAdmin(true);
        };
        checkRole();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('users')
            .select('user_id, username, first_name, last_name, avatar_url, role')
            .in('role', ['CREATOR', 'MEMBER'])
            .order('username', { ascending: true });
        if (error) setStatusMsg(t.error(error.message));
        else if (data) setUsers(data);
        setLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchUsers(); }, []);

    const filteredUsers = useMemo(() => {
        if (!search.trim()) return users;
        const q = search.toLowerCase();
        return users.filter(u => {
            const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
            return fullName.includes(q) || u.username.toLowerCase().includes(q);
        });
    }, [users, search]);

    const handleToggleRole = async (user) => {
        if (!isAdmin) return;
        const newRole = user.role === 'CREATOR' ? 'MEMBER' : 'CREATOR';
        setUpdatingId(user.user_id);

        const { error } = await supabase.from('users').update({ role: newRole }).eq('user_id', user.user_id);

        if (error) setStatusMsg(t.error(error.message));
        else {
            setUsers(prev => prev.map(u => u.user_id === user.user_id ? { ...u, role: newRole } : u));
            setStatusMsg(t.updated(user.username, newRole));
        }
        setUpdatingId(null);
    };

    return (
        <div>
            <p style={{ ...labelStyle, marginBottom: '14px' }}>{t.heading}</p>
            <p style={{ fontFamily: font, fontSize: '11.5px', color: theme.textMuted, margin: '0 0 20px' }}>
                {t.note}
            </p>

            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.search} style={{ ...inputStyle, maxWidth: '320px', marginBottom: '20px' }} />

            {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: '0 0 16px' }}>{statusMsg}</p>}

            {loading ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>
            ) : filteredUsers.length === 0 ? (
                <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.empty}</p>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '560px' }}>
                    {filteredUsers.map(u => {
                        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
                        const isCreator = u.role === 'CREATOR';
                        return (
                            <div key={u.user_id} style={{
                                display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px',
                                background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 0,
                            }}>
                                <div style={{ width: '34px', height: '34px', borderRadius: 0, overflow: 'hidden', flexShrink: 0, background: theme.bg }}>
                                    {u.avatar_url && <img src={u.avatar_url} alt={fullName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '13px', color: theme.text }}>{fullName}</p>
                                    <p style={{ margin: 0, fontFamily: font, fontSize: '10px', color: theme.textMuted }}>@{u.username}</p>
                                </div>

                                <span style={{
                                    fontSize: '10px', color: theme.textMuted, fontFamily: font,
                                    padding: '3px 9px', border: `1px solid ${theme.border}`, borderRadius: 0,
                                }}>
                                    {u.role}
                                </span>

                                {isAdmin && (
                                    <button
                                        className="wlink"
                                        onClick={() => handleToggleRole(u)}
                                        disabled={updatingId === u.user_id}
                                        style={{ ...outlineButton, opacity: updatingId === u.user_id ? 0.5 : 1, whiteSpace: 'nowrap' }}
                                    >
                                        {updatingId === u.user_id ? '...' : isCreator ? t.toMember : t.toCreator}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default AdminRoles;